import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { KaijuLoader } from '../components/KaijuLoader'

type Reader = {
  id: string
  name: string
  email: string | null
  notes: string | null
  created_at: string
}

type FeedbackItem = {
  id: string
  seq: number
  short_id: string
  batch_id: string
  task_id: string
  quote: string
  location: string | null
  severity: 'blocker' | 'major' | 'minor' | 'nit' | 'praise'
  decision: 'pending' | 'accept' | 'reject' | 'defer' | 'needs-discussion'
  decision_note: string | null
  created_at: string
  reader_id: string
  reader_name: string
  book_slug: string | null
  tracker_document_id: string | null
  received_date: string
  source_kind: string
  task_status: string
  task_title: string
}

type Stats = {
  total: number
  byDecision: Record<string, number>
  bySeverity: Record<string, number>
  byReader: Array<{ reader_id: string; reader_name: string; count: number }>
  byBook: Array<{ book_slug: string | null; count: number }>
}

type TaskComment = {
  id: string
  task_id: string
  author: string
  content: string
  created_at: string
}

const SEVERITY_ORDER = ['blocker', 'major', 'minor', 'nit', 'praise'] as const
const DECISION_ORDER = ['pending', 'accept', 'reject', 'defer', 'needs-discussion'] as const

const SEVERITY_COLOR: Record<string, string> = {
  blocker: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  major: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200',
  minor: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200',
  nit: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  praise: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
}

const DECISION_COLOR: Record<string, string> = {
  pending: 'bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-200',
  accept: 'bg-green-200 text-green-900 dark:bg-green-900/50 dark:text-green-100',
  reject: 'bg-red-200 text-red-900 dark:bg-red-900/50 dark:text-red-100',
  defer: 'bg-blue-200 text-blue-900 dark:bg-blue-900/50 dark:text-blue-100',
  'needs-discussion': 'bg-purple-200 text-purple-900 dark:bg-purple-900/50 dark:text-purple-100',
}

export function BetaFeedbackPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [readers, setReaders] = useState<Reader[]>([])
  const [items, setItems] = useState<FeedbackItem[]>([])
  const [stats, setStats] = useState<Stats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showBatchModal, setShowBatchModal] = useState(false)
  const [showReaderModal, setShowReaderModal] = useState(false)
  const [drawerItem, setDrawerItem] = useState<FeedbackItem | null>(null)

  const readerId = searchParams.get('readerId') || ''
  const bookSlug = searchParams.get('bookSlug') || ''
  const decision = searchParams.get('decision') || ''
  const severity = searchParams.get('severity') || ''
  const from = searchParams.get('from') || ''
  const to = searchParams.get('to') || ''
  const groupBy = searchParams.get('groupBy') || 'reader' // reader | book | date

  function updateParam(key: string, value: string) {
    const next = new URLSearchParams(searchParams)
    if (value) next.set(key, value)
    else next.delete(key)
    setSearchParams(next)
  }

  async function loadAll() {
    setLoading(true)
    setError(null)
    try {
      const qs = new URLSearchParams()
      if (readerId) qs.set('readerId', readerId)
      if (bookSlug) qs.set('bookSlug', bookSlug)
      if (decision) qs.set('decision', decision)
      if (severity) qs.set('severity', severity)
      if (from) qs.set('from', from)
      if (to) qs.set('to', to)

      const [readersRes, itemsRes, statsRes] = await Promise.all([
        fetch('/api/beta-feedback/readers'),
        fetch(`/api/beta-feedback/items?${qs.toString()}`),
        fetch('/api/beta-feedback/stats'),
      ])
      if (!readersRes.ok) throw new Error('Failed to load readers')
      if (!itemsRes.ok) throw new Error('Failed to load items')
      if (!statsRes.ok) throw new Error('Failed to load stats')

      const readersJson = await readersRes.json()
      const itemsJson = await itemsRes.json()
      const statsJson = await statsRes.json()
      setReaders(readersJson.readers || [])
      setItems(itemsJson.items || [])
      setStats(statsJson)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readerId, bookSlug, decision, severity, from, to])

  const bookSlugs = useMemo(() => {
    const set = new Set<string>()
    items.forEach((i) => {
      if (i.book_slug) set.add(i.book_slug)
    })
    stats?.byBook.forEach((b) => {
      if (b.book_slug) set.add(b.book_slug)
    })
    return Array.from(set).sort()
  }, [items, stats])

  const grouped = useMemo(() => {
    const groups = new Map<string, FeedbackItem[]>()
    for (const item of items) {
      let key = ''
      if (groupBy === 'reader') {
        key = `${item.reader_name} · ${item.book_slug ?? 'unassigned'}`
      } else if (groupBy === 'book') {
        key = `${item.book_slug ?? 'unassigned'} · ${item.reader_name}`
      } else {
        key = item.received_date.slice(0, 10)
      }
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key)!.push(item)
    }
    return Array.from(groups.entries())
  }, [items, groupBy])

  async function updateItemDecision(id: string, next: FeedbackItem['decision']) {
    const resp = await fetch(`/api/beta-feedback/items/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ decision: next }),
    })
    if (!resp.ok) {
      alert('Failed to update decision')
      return
    }
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, decision: next } : i)))
  }

  return (
    <div className="p-6 max-w-7xl mx-auto text-slate-900 dark:text-slate-100">
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold">Beta Reader Feedback</h1>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
            Ingest, discuss, and decide on feedback from beta readers.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowReaderModal(true)}
            className="px-3 py-2 rounded bg-slate-200 text-slate-900 hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600 text-sm"
          >
            + Reader
          </button>
          <button
            onClick={() => setShowBatchModal(true)}
            className="px-3 py-2 rounded bg-indigo-600 text-white hover:bg-indigo-700 text-sm"
          >
            + New Feedback Batch
          </button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <StatCard label="Total" value={stats.total} />
          <StatCard label="Pending" value={stats.byDecision.pending ?? 0} />
          <StatCard label="Accepted" value={stats.byDecision.accept ?? 0} />
          <StatCard label="Rejected" value={stats.byDecision.reject ?? 0} />
          <StatCard label="Blockers" value={stats.bySeverity.blocker ?? 0} />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-6 gap-3 mb-6">
        <select
          value={readerId}
          onChange={(e) => updateParam('readerId', e.target.value)}
          className="rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-sm"
        >
          <option value="">All readers</option>
          {readers.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>
        <select
          value={bookSlug}
          onChange={(e) => updateParam('bookSlug', e.target.value)}
          className="rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-sm"
        >
          <option value="">All books</option>
          {bookSlugs.map((b) => (
            <option key={b} value={b}>
              {b}
            </option>
          ))}
        </select>
        <select
          value={severity}
          onChange={(e) => updateParam('severity', e.target.value)}
          className="rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-sm"
        >
          <option value="">All severities</option>
          {SEVERITY_ORDER.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          value={decision}
          onChange={(e) => updateParam('decision', e.target.value)}
          className="rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-sm"
        >
          <option value="">All decisions</option>
          {DECISION_ORDER.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
        <input
          type="date"
          value={from}
          onChange={(e) => updateParam('from', e.target.value)}
          className="rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-sm"
          placeholder="From"
        />
        <input
          type="date"
          value={to}
          onChange={(e) => updateParam('to', e.target.value)}
          className="rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-sm"
          placeholder="To"
        />
      </div>

      <div className="flex items-center gap-2 mb-4 text-sm">
        <span className="text-slate-600 dark:text-slate-400">Group by:</span>
        {(['reader', 'book', 'date'] as const).map((g) => (
          <button
            key={g}
            onClick={() => updateParam('groupBy', g)}
            className={
              'px-2 py-1 rounded ' +
              (groupBy === g
                ? 'bg-indigo-600 text-white'
                : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300')
            }
          >
            {g}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-12 flex justify-center">
          <KaijuLoader size="md" />
        </div>
      ) : error ? (
        <div className="p-4 rounded bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200">
          {error}
        </div>
      ) : items.length === 0 ? (
        <div className="p-8 rounded border border-dashed border-slate-300 dark:border-slate-700 text-center text-slate-600 dark:text-slate-400">
          No feedback yet. Click "New Feedback Batch" to ingest reader notes.
        </div>
      ) : (
        <div className="space-y-6">
          {grouped.map(([key, groupItems]) => (
            <div key={key}>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
                {key} <span className="text-slate-400">({groupItems.length})</span>
              </h2>
              <div className="space-y-2">
                {groupItems.map((item) => (
                  <div
                    key={item.id}
                    className="p-3 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
                  >
                    <div className="flex items-start gap-3">
                      <span
                        className={'shrink-0 px-2 py-0.5 rounded text-xs font-medium ' + SEVERITY_COLOR[item.severity]}
                      >
                        {item.severity}
                      </span>
                      <span className="shrink-0 font-mono text-xs text-slate-500 dark:text-slate-400">
                        {item.short_id}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm">{item.quote}</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400 mt-1 flex flex-wrap gap-2">
                          <span>{item.reader_name}</span>
                          {item.book_slug && <span>· {item.book_slug}</span>}
                          {item.location && <span>· {item.location}</span>}
                          <span>· {item.received_date.slice(0, 10)}</span>
                          <span>· task: {item.task_status}</span>
                        </div>
                      </div>
                      <select
                        value={item.decision}
                        onChange={(e) => updateItemDecision(item.id, e.target.value as FeedbackItem['decision'])}
                        className={
                          'shrink-0 rounded border-0 px-2 py-1 text-xs font-medium ' + DECISION_COLOR[item.decision]
                        }
                      >
                        {DECISION_ORDER.map((d) => (
                          <option key={d} value={d}>
                            {d}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={() => setDrawerItem(item)}
                        className="shrink-0 px-2 py-1 rounded text-xs bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600"
                      >
                        Discuss
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {showReaderModal && (
        <ReaderModal
          onClose={() => setShowReaderModal(false)}
          onCreated={() => {
            setShowReaderModal(false)
            loadAll()
          }}
        />
      )}

      {showBatchModal && (
        <BatchModal
          readers={readers}
          onClose={() => setShowBatchModal(false)}
          onCreated={() => {
            setShowBatchModal(false)
            loadAll()
          }}
        />
      )}

      {drawerItem && (
        <DiscussDrawer item={drawerItem} onClose={() => setDrawerItem(null)} />
      )}
    </div>
  )
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="p-3 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
      <div className="text-xs text-slate-500 dark:text-slate-400 uppercase">{label}</div>
      <div className="text-2xl font-semibold">{value}</div>
    </div>
  )
}

function ReaderModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function save() {
    if (!name.trim()) return
    setSaving(true)
    setErr(null)
    try {
      const resp = await fetch('/api/beta-feedback/readers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim() || null,
          notes: notes.trim() || null,
        }),
      })
      if (!resp.ok) throw new Error((await resp.json()).error || 'Failed')
      onCreated()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal title="Add Beta Reader" onClose={onClose}>
      <div className="space-y-3">
        <Field label="Name">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1"
          />
        </Field>
        <Field label="Email (optional)">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1"
          />
        </Field>
        <Field label="Notes (optional)">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1"
          />
        </Field>
        {err && <div className="text-sm text-red-600 dark:text-red-400">{err}</div>}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1 rounded bg-slate-200 dark:bg-slate-700">
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving || !name.trim()}
            className="px-3 py-1 rounded bg-indigo-600 text-white disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

function BatchModal({
  readers,
  onClose,
  onCreated,
}: {
  readers: Reader[]
  onClose: () => void
  onCreated: () => void
}) {
  const [readerId, setReaderId] = useState(readers[0]?.id ?? '')
  const [bookSlug, setBookSlug] = useState('')
  const [trackerDocumentId, setTrackerDocumentId] = useState('')
  const [receivedDate, setReceivedDate] = useState(new Date().toISOString().slice(0, 10))
  const [mode, setMode] = useState<'paste' | 'upload'>('paste')
  const [text, setText] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function save() {
    if (!readerId) {
      setErr('Pick a reader')
      return
    }
    setSaving(true)
    setErr(null)
    try {
      let resp: Response
      if (mode === 'paste') {
        if (!text.trim()) {
          setErr('Paste some feedback text')
          setSaving(false)
          return
        }
        resp = await fetch('/api/beta-feedback/batches', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            readerId,
            bookSlug: bookSlug || null,
            trackerDocumentId: trackerDocumentId || null,
            receivedDate,
            sourceKind: 'paste',
            text,
          }),
        })
      } else {
        if (!file) {
          setErr('Pick a file')
          setSaving(false)
          return
        }
        const fd = new FormData()
        fd.append('readerId', readerId)
        fd.append('receivedDate', receivedDate)
        if (bookSlug) fd.append('bookSlug', bookSlug)
        if (trackerDocumentId) fd.append('trackerDocumentId', trackerDocumentId)
        fd.append('file', file)
        resp = await fetch('/api/beta-feedback/batches/upload', {
          method: 'POST',
          body: fd,
        })
      }
      if (!resp.ok) {
        const e = await resp.json().catch(() => ({}))
        throw new Error(e.error || `HTTP ${resp.status}`)
      }
      onCreated()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal title="New Feedback Batch" onClose={onClose}>
      <div className="space-y-3">
        <Field label="Reader">
          <select
            value={readerId}
            onChange={(e) => setReaderId(e.target.value)}
            className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1"
          >
            <option value="">Select…</option>
            {readers.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Book slug (optional)">
            <input
              value={bookSlug}
              onChange={(e) => setBookSlug(e.target.value)}
              placeholder="e.g. three-body-problem"
              className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1"
            />
          </Field>
          <Field label="Tracker doc ID (optional)">
            <input
              value={trackerDocumentId}
              onChange={(e) => setTrackerDocumentId(e.target.value)}
              placeholder="e.g. WP-IDEA-23"
              className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1"
            />
          </Field>
        </div>
        <Field label="Received date">
          <input
            type="date"
            value={receivedDate}
            onChange={(e) => setReceivedDate(e.target.value)}
            className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1"
          />
        </Field>
        <div className="flex gap-2 text-sm">
          <button
            onClick={() => setMode('paste')}
            className={'px-3 py-1 rounded ' + (mode === 'paste' ? 'bg-indigo-600 text-white' : 'bg-slate-200 dark:bg-slate-700')}
          >
            Paste text
          </button>
          <button
            onClick={() => setMode('upload')}
            className={'px-3 py-1 rounded ' + (mode === 'upload' ? 'bg-indigo-600 text-white' : 'bg-slate-200 dark:bg-slate-700')}
          >
            Upload file
          </button>
        </div>
        {mode === 'paste' ? (
          <Field label="Feedback text">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={10}
              placeholder="Paste reader notes, comments, or inline feedback…"
              className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 font-mono text-xs"
            />
          </Field>
        ) : (
          <Field label="File (PDF, DOCX, TXT)">
            <input
              type="file"
              accept=".pdf,.docx,.txt,.md"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="w-full text-sm"
            />
          </Field>
        )}
        {err && <div className="text-sm text-red-600 dark:text-red-400">{err}</div>}
        <div className="text-xs text-slate-500 dark:text-slate-400">
          Claude will atomize the text into discrete items. This may take up to a minute.
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1 rounded bg-slate-200 dark:bg-slate-700">
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="px-3 py-1 rounded bg-indigo-600 text-white disabled:opacity-50"
          >
            {saving ? 'Ingesting…' : 'Ingest'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

function DiscussDrawer({ item, onClose }: { item: FeedbackItem; onClose: () => void }) {
  const [comments, setComments] = useState<TaskComment[]>([])
  const [loading, setLoading] = useState(true)
  const [draft, setDraft] = useState('')
  const [posting, setPosting] = useState(false)

  async function load() {
    setLoading(true)
    const resp = await fetch(`/api/tasks/${item.task_id}/comments`)
    if (resp.ok) setComments(await resp.json())
    setLoading(false)
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.task_id])

  async function post() {
    if (!draft.trim()) return
    setPosting(true)
    const resp = await fetch(`/api/tasks/${item.task_id}/comments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ author: 'user', content: draft.trim() }),
    })
    setPosting(false)
    if (resp.ok) {
      setDraft('')
      load()
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-md bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-700 p-4 overflow-y-auto">
        <div className="flex justify-between items-start mb-4">
          <h3 className="font-semibold">Discuss</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800 dark:hover:text-slate-200">
            ✕
          </button>
        </div>
        <div className="mb-4 p-3 rounded bg-slate-50 dark:bg-slate-800 text-sm">
          <div className="font-mono text-xs text-slate-500 dark:text-slate-400 mb-1">{item.short_id}</div>
          <div className="font-medium mb-1">{item.task_title}</div>
          <div className="text-slate-700 dark:text-slate-300">{item.quote}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-2">
            {item.reader_name} · {item.book_slug ?? 'no book'} · {item.received_date.slice(0, 10)}
          </div>
        </div>
        <div className="space-y-2 mb-4">
          {loading ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : comments.length === 0 ? (
            <div className="text-sm text-slate-500">No comments yet.</div>
          ) : (
            comments.map((c) => (
              <div key={c.id} className="p-2 rounded border border-slate-200 dark:border-slate-700 text-sm">
                <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">
                  {c.author} · {new Date(c.created_at).toLocaleString()}
                </div>
                <div className="whitespace-pre-wrap">{c.content}</div>
              </div>
            ))
          )}
        </div>
        <div className="space-y-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            placeholder="Add a comment…"
            className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-sm"
          />
          <button
            onClick={post}
            disabled={posting || !draft.trim()}
            className="w-full px-3 py-1 rounded bg-indigo-600 text-white text-sm disabled:opacity-50"
          >
            {posting ? 'Posting…' : 'Post comment'}
          </button>
        </div>
      </div>
    </div>
  )
}

function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center p-6 bg-black/40 overflow-y-auto">
      <div className="w-full max-w-lg rounded bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-4">
        <div className="flex justify-between items-center mb-3">
          <h2 className="font-semibold">{title}</h2>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800 dark:hover:text-slate-200">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs text-slate-600 dark:text-slate-400 mb-1">{label}</div>
      {children}
    </label>
  )
}
